import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
    Card, Row, Col, Select, DatePicker, Table, Spin, Statistic, Tag, Empty,
    Typography, Descriptions, Space, Divider, Segmented, message, Input, Button,
} from "antd";
import { SearchOutlined, SwapOutlined } from "@ant-design/icons";
import {
    BarChart, Bar, LineChart, Line, XAxis, YAxis, CartesianGrid,
    Tooltip as ReTooltip, Legend, ResponsiveContainer,
} from "recharts";
import { useTranslation } from "react-i18next";
import dayjs from "dayjs";
import { useProduct } from "../../store/productContext";
import { Product, StockEntry } from "../../api/productApi";
import { fetchItemReport, ItemReportData, ItemReportRow } from "../../api/reportApi";
import PageNavigation from "../Layout/PageNavigation";
import ExcelExportButton from "./ExcelExportButton";

const { RangePicker } = DatePicker;
const { Title, Text } = Typography;

type FilterType = "all" | "sale" | "return" | "purchase";

const TYPE_COLORS: Record<string, string> = {
    SALE: "green",
    RETURN: "red",
    BUY: "blue",
};

const num = (value: number | undefined) =>
    typeof value === "number" ? value.toLocaleString(undefined, { maximumFractionDigits: 2 }) : "-";

const totalStockOf = (product: Product) =>
    (product.currentStock || []).reduce((sum: number, entry: StockEntry) => sum + (entry.qty || 0), 0);

/**
 * Item Report — one item at a time.
 *
 * The screen opens on a searchable item list; clicking an item pulls every
 * invoice that item appears on (sale, return and purchase) with the invoice
 * number, the customer, the quantity, the cost, the sale price and the profit
 * of that line, followed by the totals, the movement chart and the live stock.
 */
const ItemReport: React.FC = () => {
    const { t } = useTranslation();
    const { products, loading: productsLoading } = useProduct();

    const [search, setSearch] = useState("");
    const [productId, setProductId] = useState<string | undefined>();
    /** The item list stays open until an item is picked, and reopens on demand. */
    const [pickerOpen, setPickerOpen] = useState(true);
    const [type, setType] = useState<FilterType>("all");
    const [dateRange, setDateRange] = useState<[dayjs.Dayjs, dayjs.Dayjs] | null>(null);
    const [data, setData] = useState<ItemReportData | null>(null);
    const [loading, setLoading] = useState(false);
    const [chartMode, setChartMode] = useState<"qty" | "amount">("qty");

    const load = useCallback(async () => {
        if (!productId) {
            setData(null);
            return;
        }
        setLoading(true);
        try {
            const result = await fetchItemReport({
                productId,
                type,
                startDate: dateRange?.[0]?.format("YYYY-MM-DD"),
                endDate: dateRange?.[1]?.format("YYYY-MM-DD"),
            });
            setData(result);
        } catch (error: any) {
            const apiMessage = error?.response?.data?.message;
            message.error(apiMessage || t("item_report_failed") || "Failed to load the item report");
            setData(null);
        } finally {
            setLoading(false);
        }
    }, [productId, type, dateRange, t]);

    useEffect(() => {
        load();
    }, [load]);

    /** Name, code, SKU, barcode and category all answer the search box. */
    const matchedProducts = useMemo(() => {
        const term = search.trim().toLowerCase();
        if (!term) return products;
        return products.filter(product =>
            [product.name, product.code, product.sku, product.barcode, product.category]
                .some(field => (field || "").toString().toLowerCase().includes(term))
        );
    }, [products, search]);

    const selectItem = (id?: string) => {
        if (!id) return;
        setProductId(id);
        setPickerOpen(false);
    };

    /** Enter on a search that narrowed down to a single item opens it straight away. */
    const openOnlyMatch = () => {
        if (matchedProducts.length === 1) selectItem(matchedProducts[0].id);
    };

    const selectedProduct = useMemo(
        () => products.find(product => product.id === productId),
        [products, productId]
    );

    const pickerColumns = [
        {
            title: t("product"),
            dataIndex: "name",
            key: "name",
            sorter: (a: Product, b: Product) => (a.name || "").localeCompare(b.name || ""),
            render: (value: string) => <Text strong>{value}</Text>,
        },
        { title: t("code"), dataIndex: "code", key: "code" },
        { title: t("category"), dataIndex: "category", key: "category", render: (v?: string) => v || "-" },
        {
            title: `${t("sell_price_iqd")}`,
            dataIndex: "sellPriceInDinar",
            key: "sellPriceInDinar",
            render: num,
        },
        {
            title: `${t("sell_price_dollar")}`,
            dataIndex: "sellPriceInDollar",
            key: "sellPriceInDollar",
            render: num,
        },
        {
            title: t("total_stock"),
            key: "totalStock",
            render: (_: any, product: Product) => {
                const total = totalStockOf(product);
                return <Tag color={total > 0 ? "green" : "red"}>{num(total)}</Tag>;
            },
        },
    ];

    /** On a purchase the party is the supplier, so the header follows the filter. */
    const partyTitle =
        type === "purchase"
            ? t("supplier") || "Supplier"
            : type === "all"
                ? `${t("customer_name") || "Customer name"} / ${t("supplier") || "Supplier"}`
                : t("customer_name") || "Customer name";

    const columns = [
        {
            title: t("invoice_number") || "Invoice number",
            dataIndex: "invoiceNumber",
            key: "invoiceNumber",
            sorter: (a: ItemReportRow, b: ItemReportRow) =>
                a.invoiceNumber.localeCompare(b.invoiceNumber),
            render: (value: string) => <Text strong>{value}</Text>,
        },
        {
            title: t("invoice_type") || "Invoice type",
            dataIndex: "invoiceType",
            key: "invoiceType",
            filters: [
                { text: t("sales") || "Sale", value: "SALE" },
                { text: t("returns") || "Return", value: "RETURN" },
                { text: t("purchases") || "Buy", value: "BUY" },
            ],
            onFilter: (value: any, row: ItemReportRow) => row.invoiceType === value,
            render: (value: string) => (
                <Tag color={TYPE_COLORS[value]}>{t(`invoice_type_${value.toLowerCase()}`) || value}</Tag>
            ),
        },
        {
            title: t("date") || "Date",
            dataIndex: "date",
            key: "date",
            defaultSortOrder: "descend" as const,
            sorter: (a: ItemReportRow, b: ItemReportRow) =>
                new Date(a.date).getTime() - new Date(b.date).getTime(),
            render: (value: string) => dayjs(value).format("YYYY-MM-DD"),
        },
        {
            title: partyTitle,
            dataIndex: "party",
            key: "party",
            sorter: (a: ItemReportRow, b: ItemReportRow) => (a.party || "").localeCompare(b.party || ""),
            render: (v: string) => v || "-",
        },
        {
            title: t("qty") || "QTY",
            dataIndex: "qty",
            key: "qty",
            sorter: (a: ItemReportRow, b: ItemReportRow) => a.qty - b.qty,
            render: (value: number, row: ItemReportRow) => `${num(value)} ${t(`unit.${row.unit}`) || row.unit}`,
        },
        {
            title: t("cost") || "Cost",
            dataIndex: "cost",
            key: "cost",
            sorter: (a: ItemReportRow, b: ItemReportRow) => a.cost - b.cost,
            render: (value: number, row: ItemReportRow) => `${num(value)} ${row.currency}`,
        },
        {
            title: t("sale_price") || "Price of sale",
            dataIndex: "price",
            key: "price",
            sorter: (a: ItemReportRow, b: ItemReportRow) => a.price - b.price,
            render: (value: number, row: ItemReportRow) =>
                row.invoiceType === "BUY" ? "-" : `${num(value)} ${row.currency}`,
        },
        {
            title: t("profit") || "Profit",
            dataIndex: "profit",
            key: "profit",
            sorter: (a: ItemReportRow, b: ItemReportRow) => a.profit - b.profit,
            render: (value: number, row: ItemReportRow) =>
                row.invoiceType === "BUY" ? (
                    "-"
                ) : (
                    <Text style={{ color: value < 0 ? "#cf1322" : "#389e0d", fontWeight: 600 }}>
                        {num(value)} {row.currency}
                    </Text>
                ),
        },
    ];

    /** A single-currency report can be footed with plain sums; a mixed one can't. */
    const rowTotals = useMemo(() => {
        const rows = data?.rows || [];
        if (rows.length === 0) return null;
        const currencies = new Set(rows.map(row => row.currency));
        if (currencies.size !== 1) return null;
        return {
            currency: rows[0].currency,
            qty: rows.reduce((sum, row) => sum + (row.qty || 0), 0),
            cost: rows.reduce((sum, row) => sum + (row.cost || 0), 0),
            price: rows.reduce((sum, row) => sum + (row.price || 0), 0),
            profit: rows.reduce((sum, row) => sum + (row.profit || 0), 0),
        };
    }, [data]);

    const chartData = (data?.chart || []).map(point => ({
        month: point.month,
        [t("sales") as string]: chartMode === "qty" ? point.saleQty : point.saleAmount,
        [t("returns") as string]: chartMode === "qty" ? point.returnQty : point.returnAmount,
        [t("purchases") as string]: chartMode === "qty" ? point.purchaseQty : point.purchaseAmount,
    }));

    const profitSeries = (data?.chart || []).map(point => ({
        month: point.month,
        [t("profit") as string]: point.profit,
    }));

    const excelColumns = [
        { header: t("invoice_number") || "Invoice number", key: "invoiceNumber" as const, width: 20 },
        { header: t("invoice_type") || "Invoice type", key: "invoiceType" as const, width: 14 },
        { header: t("date") || "Date", key: (row: ItemReportRow) => dayjs(row.date).format("YYYY-MM-DD"), width: 14 },
        { header: partyTitle, key: (row: ItemReportRow) => row.party || "-", width: 22 },
        { header: t("unit") || "Unit", key: "unit" as const, width: 12 },
        { header: t("qty") || "QTY", key: "qty" as const, width: 10 },
        { header: t("cost") || "Cost", key: "cost" as const, width: 14 },
        { header: t("sale_price") || "Price of sale", key: "price" as const, width: 14 },
        { header: t("profit") || "Profit", key: "profit" as const, width: 14 },
        { header: t("currency") || "Currency", key: "currency" as const, width: 10 },
    ];

    const excelFooter = useMemo(() => {
        if (!data) return [];
        const lines: (string | number)[][] = [];
        for (const [currency, totals] of Object.entries(data.totalsByCurrency)) {
            lines.push([`${t("total_sales") || "Total sales"} (${currency})`, totals.sale.amount, `${t("qty")}: ${totals.sale.qty}`]);
            lines.push([`${t("total_returns") || "Total returns"} (${currency})`, totals.return.amount, `${t("qty")}: ${totals.return.qty}`]);
            lines.push([`${t("total_purchases") || "Total purchases"} (${currency})`, totals.purchase.amount, `${t("qty")}: ${totals.purchase.qty}`]);
            lines.push([`${t("net_profit") || "Net profit"} (${currency})`, totals.netProfit]);
        }
        lines.push([t("current_stock") || "Current stock", data.stock.currentStock]);
        return lines;
    }, [data, t]);

    return (
        <>
            <PageNavigation title={t("item_report") as string} />

            {/* ---- Step 1: find the item and click it ---- */}
            <Card style={{ margin: 16 }}>
                <Row gutter={[12, 12]} align="middle" justify="space-between">
                    <Col xs={24} md={12}>
                        <Input
                            allowClear
                            prefix={<SearchOutlined />}
                            placeholder={t("search_item") || "Search item by name or code"}
                            value={search}
                            onChange={event => setSearch(event.target.value)}
                            onPressEnter={openOnlyMatch}
                            onFocus={() => setPickerOpen(true)}
                        />
                    </Col>
                    {selectedProduct && (
                        <Col xs={24} md={12} style={{ textAlign: "end" }}>
                            <Space wrap>
                                <Tag color="blue" style={{ fontSize: 14, padding: "4px 10px" }}>
                                    {selectedProduct.name} ({selectedProduct.code})
                                </Tag>
                                <Button icon={<SwapOutlined />} onClick={() => setPickerOpen(open => !open)}>
                                    {pickerOpen
                                        ? t("hide_item_list") || "Hide list"
                                        : t("change_item") || "Change item"}
                                </Button>
                            </Space>
                        </Col>
                    )}
                </Row>

                {pickerOpen && (
                    <>
                        <Text type="secondary" style={{ display: "block", marginTop: 12 }}>
                            {t("click_item_to_open_report") || "Click an item to see every invoice it appears on"}
                        </Text>
                        <Table<Product>
                            style={{ marginTop: 8, cursor: "pointer" }}
                            rowKey={product => product.id as string}
                            loading={productsLoading}
                            columns={pickerColumns as any}
                            dataSource={matchedProducts}
                            size="small"
                            scroll={{ x: true }}
                            pagination={{ pageSize: 8, showSizeChanger: true, size: "small" }}
                            locale={{ emptyText: <Empty description={t("no_items_found") || "No item matches your search"} /> }}
                            rowClassName={product => (product.id === productId ? "ant-table-row-selected" : "")}
                            onRow={product => ({
                                onClick: () => selectItem(product.id),
                            })}
                        />
                    </>
                )}
            </Card>

            {/* ---- Step 2: the report of that one item ---- */}
            <Spin spinning={loading}>
                {!productId && (
                    <Card style={{ margin: 16 }}>
                        <Empty description={t("select_item_to_report") || "Choose an item to see its report"} />
                    </Card>
                )}

                {productId && data && (
                    <>
                        <Card style={{ margin: 16 }}>
                            <Row gutter={[12, 12]} align="middle">
                                <Col xs={24} md={8}>
                                    <Select
                                        style={{ width: "100%" }}
                                        value={type}
                                        onChange={value => setType(value as FilterType)}
                                        options={[
                                            { value: "all", label: t("all_invoice_types") || "All types" },
                                            { value: "sale", label: t("sales") },
                                            { value: "return", label: t("returns") },
                                            { value: "purchase", label: t("purchases") },
                                        ]}
                                    />
                                </Col>
                                <Col xs={24} md={10}>
                                    <RangePicker
                                        style={{ width: "100%" }}
                                        value={dateRange}
                                        onChange={dates => {
                                            if (dates && dates[0] && dates[1]) {
                                                setDateRange([dates[0], dates[1]]);
                                            } else {
                                                setDateRange(null);
                                            }
                                        }}
                                    />
                                </Col>
                                <Col xs={24} md={6}>
                                    <ExcelExportButton<ItemReportRow>
                                        block
                                        fileName={`item-report-${data.product.code || "item"}`}
                                        sheetName={t("item_report") as string}
                                        columns={excelColumns as any}
                                        rows={data.rows}
                                        footer={excelFooter}
                                        title={[
                                            `${t("item_report")}: ${data.product.name || ""} (${data.product.code || ""})`,
                                            dateRange
                                                ? `${dateRange[0].format("YYYY-MM-DD")} → ${dateRange[1].format("YYYY-MM-DD")}`
                                                : (t("all_time") as string) || "All time",
                                        ]}
                                    />
                                </Col>
                            </Row>
                        </Card>

                        <Card style={{ margin: 16 }}>
                            <Descriptions
                                title={`${data.product.name} (${data.product.code})`}
                                bordered
                                size="small"
                                column={{ xs: 1, sm: 2, md: 3 }}
                            >
                                <Descriptions.Item label={t("category")}>{data.product.category || "-"}</Descriptions.Item>
                                <Descriptions.Item label="SKU">{data.product.sku || "-"}</Descriptions.Item>
                                <Descriptions.Item label={t("barcode")}>{data.product.barcode || "-"}</Descriptions.Item>
                                <Descriptions.Item label={`${t("buy_price")} IQD`}>{num(data.product.buyPriceInDinar)}</Descriptions.Item>
                                <Descriptions.Item label={`${t("buy_price")} USD`}>{num(data.product.buyPriceInDollar)}</Descriptions.Item>
                                <Descriptions.Item label={`${t("sell_price")} IQD`}>{num(data.product.sellPriceInDinar)}</Descriptions.Item>
                                <Descriptions.Item label={`${t("sell_price")} USD`}>{num(data.product.sellPriceInDollar)}</Descriptions.Item>
                                <Descriptions.Item label={t("stock_counted_from") || "Counted from"}>
                                    {data.stock.countedFrom ? dayjs(data.stock.countedFrom).format("YYYY-MM-DD") : "-"}
                                </Descriptions.Item>
                                <Descriptions.Item label={t("invoices") || "Invoices"}>{data.rows.length}</Descriptions.Item>
                            </Descriptions>
                        </Card>

                        <Card style={{ margin: 16 }}>
                            <Title level={5} style={{ marginTop: 0 }}>
                                {t("invoices_of_item") || "Invoices containing this item"}
                            </Title>
                            <Table<ItemReportRow>
                                rowKey={row => `${row.invoiceType}-${row.invoiceId}-${row.unit}-${row.qty}-${row.price}`}
                                columns={columns as any}
                                dataSource={data.rows}
                                pagination={{ pageSize: 20, showSizeChanger: true }}
                                scroll={{ x: true }}
                                size="small"
                                locale={{ emptyText: <Empty description={t("no_invoices_for_item") || "This item is not on any invoice yet"} /> }}
                                summary={() =>
                                    rowTotals ? (
                                        <Table.Summary fixed>
                                            <Table.Summary.Row>
                                                <Table.Summary.Cell index={0} colSpan={4}>
                                                    <Text strong>{t("total")}</Text>
                                                </Table.Summary.Cell>
                                                <Table.Summary.Cell index={4}>
                                                    <Text strong>{num(rowTotals.qty)}</Text>
                                                </Table.Summary.Cell>
                                                <Table.Summary.Cell index={5}>
                                                    <Text strong>{num(rowTotals.cost)} {rowTotals.currency}</Text>
                                                </Table.Summary.Cell>
                                                <Table.Summary.Cell index={6}>
                                                    <Text strong>{num(rowTotals.price)} {rowTotals.currency}</Text>
                                                </Table.Summary.Cell>
                                                <Table.Summary.Cell index={7}>
                                                    <Text strong style={{ color: rowTotals.profit < 0 ? "#cf1322" : "#389e0d" }}>
                                                        {num(rowTotals.profit)} {rowTotals.currency}
                                                    </Text>
                                                </Table.Summary.Cell>
                                            </Table.Summary.Row>
                                        </Table.Summary>
                                    ) : null
                                }
                            />
                        </Card>

                        {/* ---- Footer: chart + totals ---- */}
                        <Card style={{ margin: 16 }}>
                            <Row justify="space-between" align="middle" style={{ marginBottom: 12 }}>
                                <Col>
                                    <Title level={5} style={{ margin: 0 }}>
                                        {t("item_movement_chart") || "Movement over time"}
                                    </Title>
                                </Col>
                                <Col>
                                    <Segmented
                                        value={chartMode}
                                        onChange={value => setChartMode(value as "qty" | "amount")}
                                        options={[
                                            { label: t("qty") as string, value: "qty" },
                                            { label: t("totalAmount") as string, value: "amount" },
                                        ]}
                                    />
                                </Col>
                            </Row>

                            {chartData.length === 0 ? (
                                <Empty description={t("no_data") || "No data"} />
                            ) : (
                                <>
                                    <div style={{ width: "100%", height: 300 }}>
                                        <ResponsiveContainer>
                                            <BarChart data={chartData}>
                                                <CartesianGrid strokeDasharray="3 3" />
                                                <XAxis dataKey="month" />
                                                <YAxis />
                                                <ReTooltip />
                                                <Legend />
                                                <Bar dataKey={t("sales") as string} fill="#16a34a" />
                                                <Bar dataKey={t("returns") as string} fill="#dc2626" />
                                                <Bar dataKey={t("purchases") as string} fill="#2563eb" />
                                            </BarChart>
                                        </ResponsiveContainer>
                                    </div>

                                    <Divider />

                                    <Title level={5}>{t("profit_over_time") || "Profit over time"}</Title>
                                    <div style={{ width: "100%", height: 250 }}>
                                        <ResponsiveContainer>
                                            <LineChart data={profitSeries}>
                                                <CartesianGrid strokeDasharray="3 3" />
                                                <XAxis dataKey="month" />
                                                <YAxis />
                                                <ReTooltip />
                                                <Legend />
                                                <Line
                                                    type="monotone"
                                                    dataKey={t("profit") as string}
                                                    stroke="#7c3aed"
                                                    strokeWidth={2}
                                                />
                                            </LineChart>
                                        </ResponsiveContainer>
                                    </div>
                                </>
                            )}

                            <Divider />

                            {Object.entries(data.totalsByCurrency).map(([currency, totals]) => (
                                <div key={currency} style={{ marginBottom: 20 }}>
                                    <Text strong style={{ fontSize: 16 }}>{currency}</Text>
                                    <Row gutter={[16, 16]} style={{ marginTop: 8 }}>
                                        <Col xs={12} md={6}>
                                            <Statistic
                                                title={t("total_sales") || "Total sales"}
                                                value={totals.sale.amount}
                                                precision={2}
                                                valueStyle={{ color: "#16a34a" }}
                                                suffix={currency}
                                            />
                                            <Text type="secondary">
                                                {t("qty")}: {num(totals.sale.qty)} · {t("invoices") || "invoices"}: {totals.sale.invoices}
                                            </Text>
                                        </Col>
                                        <Col xs={12} md={6}>
                                            <Statistic
                                                title={t("total_returns") || "Total returns"}
                                                value={totals.return.amount}
                                                precision={2}
                                                valueStyle={{ color: "#dc2626" }}
                                                suffix={currency}
                                            />
                                            <Text type="secondary">
                                                {t("qty")}: {num(totals.return.qty)} · {t("invoices") || "invoices"}: {totals.return.invoices}
                                            </Text>
                                        </Col>
                                        <Col xs={12} md={6}>
                                            <Statistic
                                                title={t("total_purchases") || "Total purchases"}
                                                value={totals.purchase.amount}
                                                precision={2}
                                                valueStyle={{ color: "#2563eb" }}
                                                suffix={currency}
                                            />
                                            <Text type="secondary">
                                                {t("qty")}: {num(totals.purchase.qty)} · {t("invoices") || "invoices"}: {totals.purchase.invoices}
                                            </Text>
                                        </Col>
                                        <Col xs={12} md={6}>
                                            <Statistic
                                                title={t("net_profit") || "Net profit"}
                                                value={totals.netProfit}
                                                precision={2}
                                                valueStyle={{ color: totals.netProfit < 0 ? "#cf1322" : "#7c3aed" }}
                                                suffix={currency}
                                            />
                                        </Col>
                                    </Row>
                                </div>
                            ))}

                            <Divider />

                            <Row gutter={[16, 16]}>
                                <Col xs={12} md={6}>
                                    <Statistic
                                        title={t("current_stock") || "Current stock"}
                                        value={data.stock.currentStock}
                                        valueStyle={{ color: data.stock.currentStock < 0 ? "#cf1322" : "#22314A" }}
                                    />
                                </Col>
                                <Col xs={12} md={6}>
                                    <Statistic title={`${t("stock_value") || "Stock value"} IQD`} value={data.stock.valueIQD} precision={0} />
                                </Col>
                                <Col xs={12} md={6}>
                                    <Statistic title={`${t("stock_value") || "Stock value"} USD`} value={data.stock.valueUSD} precision={2} />
                                </Col>
                                <Col xs={24} md={6}>
                                    <Text type="secondary">{t("stock_by_unit") || "Stock by unit"}</Text>
                                    <div style={{ marginTop: 4 }}>
                                        <Space size={[4, 4]} wrap>
                                            {Object.entries(data.stock.byUnit).map(([unit, movement]) => (
                                                <Tag key={unit} color={movement.final < 0 ? "red" : "blue"}>
                                                    {t(`unit.${unit}`) || unit}: {num(movement.final)}
                                                </Tag>
                                            ))}
                                            {Object.keys(data.stock.byUnit).length === 0 && <Tag>-</Tag>}
                                        </Space>
                                    </div>
                                </Col>
                            </Row>

                            <Divider />

                            <Table
                                title={() => t("stock_movement_breakdown") || "Stock movement breakdown"}
                                size="small"
                                pagination={false}
                                rowKey="unit"
                                dataSource={Object.entries(data.stock.byUnit).map(([unit, movement]) => ({
                                    unit,
                                    ...movement,
                                }))}
                                columns={[
                                    { title: t("unit"), dataIndex: "unit", render: (u: string) => t(`unit.${u}`) || u },
                                    { title: t("opening_stock") || "Opening", dataIndex: "initial", render: num },
                                    { title: t("purchases"), dataIndex: "bought", render: num },
                                    { title: t("returns"), dataIndex: "returned", render: num },
                                    { title: t("sales"), dataIndex: "sold", render: num },
                                    {
                                        title: t("current_stock") || "Current stock",
                                        dataIndex: "final",
                                        render: (value: number) => (
                                            <Text strong style={{ color: value < 0 ? "#cf1322" : "#389e0d" }}>{num(value)}</Text>
                                        ),
                                    },
                                ]}
                            />
                        </Card>
                    </>
                )}
            </Spin>
        </>
    );
};

export default ItemReport;
